import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { dsrApi, organizationApi } from '../services/api';
import {
  DataSubjectRequest,
  DataSubjectRequestType,
  DataSubjectRequestStatus,
  Organization
} from '@dsgvo-buddy/types';
import {
  Title,
  Button,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Input,
  Select,
  Option,
  FlexBox,
  Text,
  Icon,
  ObjectStatus,
  BusyIndicator,
  Card,
  CardHeader
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import PageIntro from '../components/PageIntro';
import { pageIntros } from '../helpers/helpTexts';

const requestTypeConfig: Record<DataSubjectRequestType, { label: string }> = {
  [DataSubjectRequestType.ACCESS]: { label: 'Auskunft' },
  [DataSubjectRequestType.RECTIFICATION]: { label: 'Berichtigung' },
  [DataSubjectRequestType.ERASURE]: { label: 'Löschung' },
  [DataSubjectRequestType.RESTRICTION]: { label: 'Einschränkung' },
  [DataSubjectRequestType.PORTABILITY]: { label: 'Datenübertragbarkeit' },
  [DataSubjectRequestType.OBJECTION]: { label: 'Widerspruch' }
};

const statusConfig: Record<DataSubjectRequestStatus, { label: string; state: string }> = {
  [DataSubjectRequestStatus.NEW]: { label: 'Neu', state: 'Information' },
  [DataSubjectRequestStatus.IDENTITY_CHECK]: { label: 'Identitätsprüfung', state: 'Warning' },
  [DataSubjectRequestStatus.IN_PROGRESS]: { label: 'In Bearbeitung', state: 'Information' },
  [DataSubjectRequestStatus.QUERY_OPEN]: { label: 'Rückfrage offen', state: 'Warning' },
  [DataSubjectRequestStatus.APPROVAL]: { label: 'Freigabe', state: 'Information' },
  [DataSubjectRequestStatus.COMPLETED]: { label: 'Erledigt', state: 'Success' },
  [DataSubjectRequestStatus.REJECTED]: { label: 'Abgelehnt', state: 'Error' }
};

function getDeadlineState(dsr: DataSubjectRequest): { color: string; label: string } {
  const deadline = dsr.extended_deadline_at || dsr.deadline_at;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diffMs = deadlineDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { color: 'var(--sapNegativeColor)', label: `${Math.abs(diffDays)} Tage überfällig` };
  if (diffDays <= 5) return { color: 'var(--sapCriticalColor)', label: `Noch ${diffDays} Tage` };
  return { color: 'var(--sapNeutralTextColor)', label: `${diffDays} Tage` };
}

export default function DsrListPage() {
  const navigate = useNavigate();
  const { canCreate } = useUserRole();
  const [requests, setRequests] = useState<DataSubjectRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<DataSubjectRequest[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<DataSubjectRequestType | ''>('');
  const [statusFilter, setStatusFilter] = useState<DataSubjectRequestStatus | ''>('');

  useEffect(() => { loadOrganizations(); }, []);
  useEffect(() => { if (currentOrg) { loadRequests(); } }, [currentOrg]);
  useEffect(() => { applyFilters(); }, [requests, searchTerm, typeFilter, statusFilter]);

  const loadOrganizations = async () => {
    const response = await organizationApi.getAll();
    if (response.success && response.data) {
      if (response.data.length > 0) setCurrentOrg(response.data[0]);
    }
    setLoading(false);
  };

  const loadRequests = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const response = await dsrApi.getByOrganization(currentOrg.id);
    if (response.success && response.data) setRequests(response.data);
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...requests];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.subject_name.toLowerCase().includes(term) ||
        r.subject_email?.toLowerCase().includes(term)
      );
    }
    if (typeFilter) filtered = filtered.filter(r => r.request_type === typeFilter);
    if (statusFilter) filtered = filtered.filter(r => r.status === statusFilter);
    setFilteredRequests(filtered);
  };

  if (loading && !currentOrg) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout">
      {/* Page Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>Betroffenenrechte</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Anfragen betroffener Personen gemäß Art. 12-22 DSGVO
          </Text>
        </div>
        {canCreate && (
          <Button icon="add" design="Emphasized" onClick={() => navigate('/data-subject-requests/new')}>
            Neue Anfrage
          </Button>
        )}
      </FlexBox>

      <PageIntro {...pageIntros['data-subject-requests']} storageKey="dsr" />

      {/* Filter Bar */}
      <FlexBox
        alignItems="End"
        wrap="Wrap"
        style={{
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '1rem',
          background: 'var(--sapObjectHeader_Background, var(--sapBaseColor))',
          borderRadius: '0.5rem',
          border: '1px solid var(--sapGroup_TitleBorderColor, #d9d9d9)'
        }}
      >
        <div style={{ flex: '1 1 300px', minWidth: '200px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Suche</Text>
          <Input
            placeholder="Name, E-Mail..."
            icon={<Icon name="search" />}
            value={searchTerm}
            onInput={(e: any) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ flex: '0 0 220px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Anfragetyp</Text>
          <Select onChange={(e: any) => setTypeFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Typen</Option>
            {Object.entries(requestTypeConfig).map(([key, cfg]) => (
              <Option key={key} value={key}>{cfg.label}</Option>
            ))}
          </Select>
        </div>

        <div style={{ flex: '0 0 180px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Status</Text>
          <Select onChange={(e: any) => setStatusFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Status</Option>
            {Object.entries(statusConfig).map(([key, cfg]) => (
              <Option key={key} value={key}>{cfg.label}</Option>
            ))}
          </Select>
        </div>

        {(searchTerm || typeFilter || statusFilter) && (
          <Button
            icon="decline"
            design="Transparent"
            onClick={() => { setSearchTerm(''); setTypeFilter(''); setStatusFilter(''); }}
            tooltip="Filter zurücksetzen"
          />
        )}
      </FlexBox>

      {/* Table Card */}
      <Card style={{ overflow: 'hidden' }}
        header={
          <CardHeader
            titleText={`${filteredRequests.length} von ${requests.length} Anfragen`}
            subtitleText="Klicken Sie auf eine Zeile für Details"
            avatar={<Icon name="person-placeholder" />}
          />
        }
      >
        {loading ? (
          <FlexBox alignItems="Center" justifyContent="Center" style={{ padding: '3rem' }}>
            <BusyIndicator active size="Large" />
          </FlexBox>
        ) : filteredRequests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Icon name="person-placeholder" style={{ fontSize: '3rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '1rem' }} />
            <Title level="H5" style={{ marginBottom: '0.5rem' }}>
              {requests.length === 0 ? 'Noch keine Anfragen' : 'Keine Treffer'}
            </Title>
            <Text style={{ display: 'block', color: 'var(--sapNeutralTextColor)', marginBottom: '1rem' }}>
              {requests.length === 0
                ? 'Erfassen Sie Ihre erste Betroffenenanfrage.'
                : 'Passen Sie Ihre Filterkriterien an.'}
            </Text>
            {requests.length === 0 && canCreate && (
              <Button icon="add" design="Emphasized" onClick={() => navigate('/data-subject-requests/new')}>
                Neue Anfrage erstellen
              </Button>
            )}
          </div>
        ) : (
          <div style={{ padding: '0 1rem 1rem' }}>
            <Table
              style={{ width: '100%' }}
              onRowClick={(e: any) => {
                const index = parseInt(e.detail.row.dataset.index);
                navigate(`/data-subject-requests/${filteredRequests[index].id}`);
              }}
              columns={
                <>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Betroffener</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Eingang</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Frist</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Status</Text></TableColumn>
                  <TableColumn demandPopin minWidth={600}><Text style={{ fontWeight: '600' }}>Zugewiesen</Text></TableColumn>
                </>
              }
            >
              {filteredRequests.map((dsr, index) => {
                const deadlineInfo = getDeadlineState(dsr);
                const isTerminal = dsr.status === DataSubjectRequestStatus.COMPLETED || dsr.status === DataSubjectRequestStatus.REJECTED;
                return (
                  <TableRow key={dsr.id} data-index={index} type="Active">
                    <TableCell>
                      <div>
                        <Text style={{ fontWeight: '600' }}>{dsr.subject_name}</Text>
                        <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginTop: '0.125rem' }}>
                          {requestTypeConfig[dsr.request_type]?.label ?? dsr.request_type}
                        </Text>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Text>{new Date(dsr.received_at).toLocaleDateString('de-DE')}</Text>
                    </TableCell>
                    <TableCell>
                      <Text style={{ color: isTerminal ? 'var(--sapNeutralTextColor)' : deadlineInfo.color, fontWeight: isTerminal ? 'normal' : '600' }}>
                        {isTerminal
                          ? new Date(dsr.deadline_at).toLocaleDateString('de-DE')
                          : deadlineInfo.label}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <ObjectStatus state={statusConfig[dsr.status]?.state as any} showDefaultIcon>
                        {statusConfig[dsr.status]?.label ?? dsr.status}
                      </ObjectStatus>
                    </TableCell>
                    <TableCell>
                      <Text>{dsr.assigned_to ?? '-'}</Text>
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
